/**
 * CTO Common report (task 073-02, epic 073 D4) — "View TS Common Leads", the
 * between-traffics view: which leads appear under more than one traffic
 * source. One row per SELECTED source — total / unique / common (the address
 * also captured in-range under a different selected source, epic 073 D2) /
 * success / duplicate / not-checked — plus the labeled total row (the
 * selection grain's recomputed measures, never the column sums: an address
 * common to two sources counts once).
 *
 * Sources control: a checkbox list with Select All, plain native controls —
 * no multi-select component exists in the repo (r1 M-4). Its options are fed
 * from the response's own rows (the cto-stats precedent, `cto-stats.tsx`),
 * so a source with zero in-range leads is not listed (stated limitation;
 * legacy listed all sources) and narrowing the selection narrows the list
 * back — Select All restores every source. Empty selection = all; the single
 * stats request carries the selection as repeated `siteIds` params.
 *
 * Default range: first day of last month → today (legacy parity,
 * `view-cto-ts-leads-page.php`) — so the shared preset strip is replaced by
 * an always-visible range field. Downloads: per-source Unique / All are
 * links to the EXISTING `cto-leads.csv` (r1 H-2, the CTO Email screen's own
 * export); Export stats streams task 01's whole-report `cto-common-stats.csv`.
 */

import { useEffect, useState } from 'react';
import { DateRangeField } from '../../components/date-range-field';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  fetchCtoCommonStats,
  ctoCommonLeadsExportUrl,
  ctoCommonStatsExportUrl,
  type CtoCommonLeadsVariant,
  type CtoCommonSourceRow,
  type CtoCommonStatsResponse,
} from './cto-common-api';
import { ReportShell } from './report-shell';
import { TestLeadsToggle } from './report-filters';
import { cell, EmptyRow, ErrorBanner, LoadingRow, UNIQUE_TOTAL_TITLE } from './report-table';

const pad2 = (n: number) => String(n).padStart(2, '0');
const fmtYmd = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/**
 * First day of the previous calendar month → today, in the browser's local
 * zone (the legacy default range). Screen-local because `date-presets.ts`'s
 * two presets bracket months, and this screen is outside its pen.
 */
export function legacyDefaultRange(): [string, string] {
  const now = new Date();
  const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return [fmtYmd(firstOfLastMonth), fmtYmd(now)];
}

/** Column-help strings — her language, no internal ids (the A41/Q-rule). */
const COMMON_COLUMN_HELP =
  'Distinct addresses of this source that are also captured under another selected source in the range';
const UNIQUE_COLUMN_HELP = "Distinct addresses among this source's leads in the range";

const COLSPAN = 9; // Site ID, Traffic source, Total leads, Unique, Common, Success, Duplicate, Not checked, Exports

/** The two per-source download links — `window.open`s the scoped `cto-leads.csv`. */
function DownloadButtons({
  idSuffix,
  onDownload,
}: {
  idSuffix: string;
  onDownload: (variant: CtoCommonLeadsVariant) => void;
}) {
  const labels: readonly [CtoCommonLeadsVariant, string][] = [
    ['unique', 'Unique'],
    ['all', 'All'],
  ];
  return (
    <div className="flex justify-end gap-1">
      {labels.map(([variant, label]) => (
        <button
          key={variant}
          type="button"
          data-testid={`cto-common-export-${variant}-${idSuffix}`}
          onClick={() => onDownload(variant)}
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function CtoCommon() {
  const [defaultFrom, defaultTo] = legacyDefaultRange();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [includeTest, setIncludeTest] = useState(false);
  // The multi-source selection; EMPTY = every source (the Select-All case) —
  // the request then sends no `siteIds` param at all (epic 073 D1).
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [data, setData] = useState<CtoCommonStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sourceOptions = data?.sources ?? [];
  const allSelected = selectedSiteIds.length === 0;

  // Fetch on every filter change; the stale response of a superseded filter
  // set is dropped rather than rendered (co-op-counts.tsx's idiom). One call
  // carries the selection — the identity of `selectedSiteIds` only changes
  // when a checkbox toggles it.
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCtoCommonStats({ from, to, includeTest, siteIds: selectedSiteIds })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest, selectedSiteIds]);

  const toggleSelectAll = () => {
    // Empty selection IS all — clicking Select All (re-)selects every source.
    setSelectedSiteIds([]);
  };

  const toggleSource = (siteId: string) => {
    setSelectedSiteIds((prev) => {
      if (prev.length === 0) {
        // Leaving the all-state: seed with every listed source minus this one.
        return sourceOptions.map((s) => s.siteId).filter((id) => id !== siteId);
      }
      return prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId];
    });
  };

  const downloadSource = (source: CtoCommonSourceRow) => (variant: CtoCommonLeadsVariant) => {
    window.open(ctoCommonLeadsExportUrl({ from, to, includeTest, siteId: source.siteId, variant }));
  };

  const exportStats = () => {
    window.open(ctoCommonStatsExportUrl({ from, to, includeTest, siteIds: selectedSiteIds }));
  };

  return (
    <ReportShell
      title="CTO Common"
      desc="Leads under more than one traffic source — one row per selected source, the total recomputed across the selection. Sources with no leads in the range are not listed. Test leads hidden by default."
      actions={
        <Button data-testid="cto-common-export-stats" onClick={exportStats}>
          Export stats
        </Button>
      }
    >
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-start gap-3 border-b border-line px-4 py-3">
          {/* The legacy default range makes the presets pointless — a plain
              always-visible range field instead of the shared preset strip. */}
          <div className="w-64">
            <DateRangeField
              from={from}
              to={to}
              onChange={(f, t) => {
                setFrom(f);
                setTo(t);
              }}
            />
          </div>

          {/* The multi-source control (r1 M-4): native checkboxes + Select All.
              Options are the response's own rows — zero-traffic sources are
              unlisted, and a narrowed selection narrows the list back. */}
          <div
            className="flex flex-col gap-1"
            role="group"
            aria-label="Traffic sources"
            data-testid="cto-common-source-filter"
          >
            <label className="flex items-center gap-2 text-sm font-medium text-ink">
              <input
                type="checkbox"
                data-testid="cto-common-source-select-all"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              Select All
            </label>
            <div className="max-h-40 w-60 overflow-y-auto rounded-md bg-surface p-2 hairline">
              {sourceOptions.length === 0 ? (
                <p className="text-xs text-faint">No sources in this range.</p>
              ) : (
                sourceOptions.map((s) => (
                  <label key={s.siteId} className="flex items-center gap-2 py-0.5 text-sm text-ink">
                    <input
                      type="checkbox"
                      data-testid={`cto-common-source-${s.siteId}`}
                      checked={allSelected || selectedSiteIds.includes(s.siteId)}
                      onChange={() => toggleSource(s.siteId)}
                    />
                    {`${s.label} (${s.siteCode})`}
                  </label>
                ))
              )}
            </div>
          </div>

          <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />

          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>

        {error && <ErrorBanner message={error} />}

        <div className="overflow-x-auto">
          <Table data-testid="cto-common-table">
            <TableHeader>
              <TableRow>
                <TableHead>Site ID</TableHead>
                <TableHead>Traffic source</TableHead>
                <TableHead className="text-right">Total leads</TableHead>
                <TableHead className="text-right" title={UNIQUE_COLUMN_HELP}>
                  Unique
                </TableHead>
                <TableHead className="text-right" title={COMMON_COLUMN_HELP}>
                  Common
                </TableHead>
                <TableHead className="text-right">Success</TableHead>
                <TableHead className="text-right">Duplicate</TableHead>
                <TableHead className="text-right">Not checked</TableHead>
                <TableHead className="text-right">Download leads</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null ? (
                <LoadingRow colSpan={COLSPAN} />
              ) : sourceOptions.length === 0 ? (
                <EmptyRow colSpan={COLSPAN} message="No leads in this range." />
              ) : (
                data.sources.map((source) => (
                  <TableRow key={source.siteId} data-testid={`cto-common-row-${source.siteId}`}>
                    <TableCell>{source.siteCode}</TableCell>
                    <TableCell>{source.label}</TableCell>
                    <TableCell className="text-right tabular-nums">{cell(source.leads)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(source.uniqueLeads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={COMMON_COLUMN_HELP}>
                      {cell(source.commonLeads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(source.success)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(source.duplicate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(source.unchecked)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DownloadButtons
                        idSuffix={source.siteId}
                        onDownload={downloadSource(source)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {data !== null && sourceOptions.length > 0 && (
              <TableFooter>
                <TableRow data-testid="cto-common-totals-row">
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.leads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.uniqueLeads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.commonLeads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.success)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.duplicate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.unchecked)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      </Card>
    </ReportShell>
  );
}
